import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import classNames from 'classnames';
import { animated, useSpring } from '@react-spring/web'

import { ShoppingCart } from 'feather-icons-react';

import formatNumber from '../../utils/formatNumber';

export default function Block({ nonce }) {
  const [cart, setCart] = useState(null);
  const [showCart, setShowCart] = useState(false);

  const { opacity, transform } = useSpring({
    opacity: showCart ? 100 : 0,
    transform: showCart ? 0 : 100,
  })

  useEffect(() => {
    fetchCart();
  }, []);

  useEffect(() => {
    const onAddToCart = () => {
      fetchCart();
    }

    window.addEventListener('add-to-cart', onAddToCart);

    return () => {
      window.removeEventListener('add-to-cart', onAddToCart);
    }
  }, [])

  const fetchCart = async () => {
    const response = await apiFetch({
      path: '/wc/store/v1/cart',
    });

    setCart(response);
  }

  const handleCartItemQuantityChange = async (key, quantity) => {
    const response = await apiFetch({
      path: `/wc/store/v1/cart/update-item?key=${key}&quantity=${quantity}`,
      method: 'POST',
      headers: {
        'Nonce': nonce,
      },
    });

    setCart(response);

    notifyCartUpdate();
  }

  const handleCartItemRemove = async (key) => {
    const response = await apiFetch({
      path: `/wc/store/v1/cart/remove-item?key=${key}`,
      method: 'POST',
      headers: {
        'Nonce': nonce,
      },
    });

    setCart(response);

    notifyCartUpdate();
  }

  const notifyCartUpdate = () => {
    window.dispatchEvent(new CustomEvent('add-to-cart'));
  }

  return (
    <>
      <div className="relative flex items-center justify-end mr-2">
        <ShoppingCart
          className="cursor-pointer stroke-[#666] hover:stroke-[#bb9e40] duration-200 size-5"
          onClick={() => setShowCart(true)}
        />
        {
          cart?.items_count > 0 && (
            <div className="absolute -top-2 -right-2 bg-[#E69900] text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {cart.items_count}
            </div>
          )
        }
      </div>
      <animated.div
        className={classNames('hidden fixed top-0 left-0 right-0 bottom-0 bg-[#000]/30 backdrop-blur z-[10]', {
          '!block': showCart,
        })}
        style={{
          opacity: opacity.to((value) => value / 100),
        }}
        onClick={() => setShowCart(false)}
      />
      <animated.div
        className={classNames('fixed top-0 right-0 bottom-0 z-[11] w-full lg:w-[400px]')}
        style={{
          transform: transform.to((value) => `translateX(${value}vw)`),
        }}
      >
        <div className="bg-white h-full flex flex-col justify-start px-6">
          <div className="py-8 flex justify-between">
            <h2 className="text-xl">Your Cart</h2>
            <div className="flex items-center">
              <svg
                className="cursor-pointer"
                onClick={() => setShowCart(!showCart)}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line x1="2.06066" y1="2.01077" x2="18.7749" y2="18.7251" stroke="#000000" strokeWidth="3"/>
                <line x1="1.22547" y1="18.7251" x2="17.9398" y2="2.01077" stroke="#000000" strokeWidth="3"/>
              </svg>
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-y-8 overflow-y-auto">
            {
              cart?.items.map(cartItem => (
                <div key={cartItem.key} className="w-full grid grid-cols-[70px_1fr_70px] gap-x-4">
                  <div>
                    <img src={cartItem.images?.[0]?.src ?? 'https://placehold.co/64'} />
                  </div>
                  <div>
                    <p className="mb-4"><a href={cartItem.permalink}>{cartItem.name}</a></p>
                    <div className="mb-4">
                      <p className={classNames({
                        'text-red-500': cartItem.prices.regular_price !== cartItem.prices.sale_price,
                      })}>${formatNumber(cartItem.prices.price)}</p>
                      {
                        cartItem.prices.regular_price !== cartItem.prices.sale_price && (
                          <p className="line-through text-gray-500 text-sm">${formatNumber(cartItem.prices.regular_price)}</p>
                        )
                      }
                    </div>
                    <div className="mb-2">
                      <select
                        className="border border-gray-300 rounded-md px-2 py-1 w-16 text-center"
                        value={cartItem.quantity}
                        onChange={((e) => handleCartItemQuantityChange(cartItem.key, e.target.value))}
                      >
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                        <option value="8">8</option>
                      </select>
                    </div>
                    <div>
                      <div
                        className="text-xs underline underline-offset-1 cursor-pointer"
                        onClick={() => handleCartItemRemove(cartItem.key)}
                      >
                        Remove
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div>${formatNumber(cartItem.totals.line_subtotal)}</div>
                  </div>
                </div>
              ))
            }
          </div>
          <div className="pt-4 pb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-xl font-bold">Sub Total</div>
                <div className="text-sm text-gray-500">Shipping & discount will be calculated at checkout</div>
              </div>
              <div>${formatNumber(cart?.totals.total_price)}</div>
            </div>
            <div className="grid grid-cols-2 gap-x-4">
              <a className="px-4 py-3 text-center border hover:bg-[#E69900] hover:text-white duration-300" href="/cart">Cart</a>
              <a className="px-4 py-3 text-center border bg-[#32373C] text-white duration-300" href="/checkout">Checkout</a>
            </div>
          </div>
        </div>
      </animated.div>
    </>
  )
}
